/**
 * @fileoverview Letterhead Bundle Manager
 *
 * One UI to upload and configure letterheads. Each upload becomes a
 * "bundle" of four assets (Header / Footer / Left side / Right side)
 * sharing a name (e.g. "Morgan"). The user can have any number of
 * bundles; each bundle's zones go into the asset library that the
 * variants section below pulls from.
 *
 * The card layout mirrors the original single-letterhead UI: per-zone
 * preview, page rule dropdown, replace button, trash. The difference is
 * we now show N of these stacked, one per uploaded letterhead.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { convexQuery, useConvexMutation } from "@convex-dev/react-query";
import type { FunctionReturnType } from "convex/server";
import { useUploadFiles } from "@xixixao/uploadstuff/react";
import { api } from "~/convex/_generated/api";
import type { Id } from "~/convex/_generated/dataModel";
import { Button, Input, Spinner } from "@/ui/primitives";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/ui/primitives/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/ui/composed/overlays/alert-dialog";
import { toast } from "sonner";
import {
  ChevronDown,
  ChevronRight,
  ChevronsDownUp,
  ChevronsUpDown,
  Eye,
  FileUp,
  Pencil,
  Star,
  Trash2,
  Upload,
} from "lucide-react";
import { cn } from "@/utils/misc";
import { extractErrorMessage } from "@/utils/errors";
import {
  PendingExtractionPreview,
  SliderNudgeButton,
} from "@/components/branding/PendingExtractionPreview";
import {
  seedFromAssets,
  usePendingExtraction,
  zoneSaveTargets,
  type ZoneSaveTarget,
} from "@/components/branding/usePendingExtraction";
import {
  clampTopBlockHeightPct,
  DEFAULT_TOP_BLOCK_HEIGHT_PCT,
  normalizeSideCrop,
  paddingAsSource,
  resolveDocumentPadding,
  variantPaddingTiers,
  type DocumentPadding,
  type PageRule,
  type SideFit,
} from "@/utils/branding/resolve-letterhead";
import { buildBundleFragments } from "@/utils/branding/letterheadFragments";
import { derivePageLayoutCombos } from "@/utils/branding/layoutCombos";
import { LetterheadSamplePreview } from "@/components/branding/LetterheadSamplePreview";

/**
 * Page rules an asset can carry as its "default". Mirrors the variant
 * layer dropdown — `none` isn't selectable here either (an off-zone is
 * just an asset that doesn't exist).
 */
const ASSET_DEFAULT_PAGE_RULES: Array<{ value: PageRule; label: string }> = [
  { value: "all", label: "All pages" },
  { value: "firstOnly", label: "First page only" },
  { value: "notFirst", label: "All except first page" },
  { value: "oddOnly", label: "Odd pages only" },
  { value: "evenOnly", label: "Even pages only" },
];

type UploadResponse = { storageId: Id<"_storage"> };
type ZoneKind = "header" | "footer" | "leftSide" | "rightSide";

const ZONE_ORDER: Array<{
  kind: ZoneKind;
  label: string;
  tall?: boolean;
}> = [
  { kind: "header", label: "Header" },
  { kind: "footer", label: "Footer" },
  { kind: "leftSide", label: "Left side", tall: true },
  { kind: "rightSide", label: "Right side", tall: true },
];

// ---------------------------------------------------------------------------
// Top-level component
// ---------------------------------------------------------------------------

export function LetterheadAssetsManager({
  orgId,
  canEdit,
}: {
  orgId: Id<"organizations">;
  canEdit: boolean;
}) {
  const { data: assets } = useQuery({
    ...convexQuery(api.settings.branding.assetQueries.listAssetsForOrg, {
      orgId,
    }),
    enabled: !!orgId,
  });

  // Group assets into bundles. Assets that share a `sourceDocumentStorageId`
  // (or share a `bundleName` when there's no source doc) are one bundle.
  // Standalone assets (single-image uploads) become their own bundle.
  const bundles = useMemo(() => groupAssetsIntoBundles(assets ?? []), [assets]);

  // brandingProfile carries the firm-default pointer and the favourite
  // bundle name list. Both feed the per-bundle action toggles below.
  const { data: brandingProfile } = useQuery({
    ...convexQuery(api.settings.branding.queries.getBrandingProfile, { orgId }),
    enabled: !!orgId,
  });
  const firmDefaultBundleName =
    brandingProfile?.firmDefaultLetterheadBundleName ?? null;
  const favouriteBundleNames: string[] =
    brandingProfile?.favouriteLetterheadBundleNames ?? [];

  // Firm-level margins — the padding every preview on this page renders
  // with (bundles have no margins of their own).
  const inheritedPadding = useMemo(
    () => resolveDocumentPadding(brandingProfile ?? null),
    [brandingProfile],
  );

  const [allCollapsed, setAllCollapsed] = useState(true);

  return (
    <div className="flex flex-col gap-4 px-6 py-4">
      <div>
        <h2 className="text-base font-medium text-primary/70">Letterheads</h2>
        <p className="mt-0.5 text-xs text-primary/40">
          Upload your firm's letterheads separately. Use the Variants section
          below to set rules for how each letterhead should apply or mix and
          match letterheads using Variants.
        </p>
      </div>

      {canEdit && (
        <NewBundleUploadCard
          orgId={orgId}
          inheritedPadding={inheritedPadding}
        />
      )}

      {bundles.length === 0 ? (
        <p className="rounded-lg border border-dashed border-border bg-secondary/20 px-4 py-8 text-center text-sm text-primary/40">
          No letterheads yet — upload a PDF above to add one.
        </p>
      ) : (
        <div className="flex flex-col gap-4">
          <div className="flex items-center justify-between">
            <h4 className="font-sans text-xs font-medium tracking-wide text-primary/40">
              Your letterheads
            </h4>
            <button
              type="button"
              onClick={() => setAllCollapsed((c) => !c)}
              className="flex items-center gap-1 rounded px-2 py-1 text-[11px] text-primary/50 transition hover:bg-secondary hover:text-primary/70"
              title={allCollapsed ? "Expand all" : "Collapse all"}
            >
              {allCollapsed ? (
                <ChevronsUpDown className="size-3.5" />
              ) : (
                <ChevronsDownUp className="size-3.5" />
              )}
              {allCollapsed ? "Expand all" : "Collapse all"}
            </button>
          </div>
          {bundles.map((bundle) => (
            <BundleCard
              key={bundle.bundleKey}
              bundle={bundle}
              orgId={orgId}
              canEdit={canEdit}
              isFirmDefault={firmDefaultBundleName === bundle.name}
              isFavourite={favouriteBundleNames.includes(bundle.name)}
              forceCollapsed={allCollapsed}
              inheritedPadding={inheritedPadding}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Bundling
// ---------------------------------------------------------------------------

type BundleAsset = FunctionReturnType<
  typeof api.settings.branding.assetQueries.listAssetsForOrg
>[number];
type Bundle = {
  bundleKey: string;
  name: string;
  sourceDocumentName?: string;
  byZone: Record<ZoneKind, BundleAsset | undefined>;
};

function groupAssetsIntoBundles(assets: BundleAsset[]): Bundle[] {
  const map = new Map<string, Bundle>();
  for (const asset of assets) {
    // Prefer explicit bundleName; fall back to sourceDocumentStorageId so
    // legacy assets created without bundleName still group sensibly.
    const key =
      asset.bundleName ||
      (asset.sourceDocumentStorageId
        ? `src:${asset.sourceDocumentStorageId}`
        : `solo:${asset._id}`);

    const existingBundle = map.get(key);
    const bundle: Bundle =
      existingBundle ??
      ({
        bundleKey: key,
        name:
          asset.bundleName ||
          asset.sourceDocumentName ||
          stripZoneSuffix(asset.name) ||
          "Untitled letterhead",
        sourceDocumentName: asset.sourceDocumentName,
        byZone: {
          header: undefined,
          footer: undefined,
          leftSide: undefined,
          rightSide: undefined,
        },
      } satisfies Bundle);
    if (!existingBundle) map.set(key, bundle);
    bundle.byZone[asset.zoneKind] = asset;
  }
  return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
}

function stripZoneSuffix(name: string): string {
  return name.replace(/\s*—\s*(Header|Footer|Left side|Right side)\s*$/i, "");
}

// ---------------------------------------------------------------------------
// New bundle upload card — drop a PDF, name the bundle, auto-crop four zones
// ---------------------------------------------------------------------------

function NewBundleUploadCard({
  orgId,
  inheritedPadding,
}: {
  orgId: Id<"organizations">;
  inheritedPadding: DocumentPadding;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [bundleName, setBundleName] = useState("");
  const extraction = usePendingExtraction();
  const { pending, rendering } = extraction;

  const generateUploadUrl = useConvexMutation(
    api.settings.branding.mutations.generateUploadUrl,
  );
  const { mutateAsync: createAssetsFromPdf } = useMutation({
    mutationFn: useConvexMutation(
      api.settings.branding.assetMutations.createAssetsFromPdf,
    ),
  });
  const { mutateAsync: updateAsset } = useMutation({
    mutationFn: useConvexMutation(
      api.settings.branding.assetMutations.updateAsset,
    ),
  });

  const pendingUploadRef = useRef<{
    sourceFile: File;
    targets: Partial<Record<ZoneKind, ZoneSaveTarget>>;
    name: string;
  } | null>(null);

  const { startUpload } = useUploadFiles(generateUploadUrl, {
    onUploadComplete: async (uploaded) => {
      if (inputRef.current) inputRef.current.value = "";
      const ctx = pendingUploadRef.current;
      try {
        if (!ctx) throw new Error("Missing upload context");
        const find = (f: File | null | undefined) =>
          f ? uploaded.find((u) => u.name === f.name) : undefined;
        const src = find(ctx.sourceFile);
        if (!src) throw new Error("Source upload missing");
        const idFor = (zone: ZoneKind) => {
          const hit = find(ctx.targets[zone]?.file);
          return hit ? (hit.response as UploadResponse).storageId : undefined;
        };

        const created = await createAssetsFromPdf({
          orgId,
          namePrefix: ctx.name,
          sourceDocumentStorageId: (src.response as UploadResponse).storageId,
          sourceDocumentName: ctx.sourceFile.name,
          headerImageStorageId: idFor("header"),
          footerImageStorageId: idFor("footer"),
          leftSideImageStorageId: idFor("leftSide"),
          rightSideImageStorageId: idFor("rightSide"),
          leftSidePageRule: ctx.targets.leftSide?.defaultPageRule,
          rightSidePageRule: ctx.targets.rightSide?.defaultPageRule,
        });
        // Remember where each zone was selected (so the editor reopens
        // there) and apply the side-block settings for flow-beside headers.
        const createdIds: Record<ZoneKind, Id<"letterheadAssets"> | null> = {
          header: created.headerAssetId,
          footer: created.footerAssetId,
          leftSide: created.leftSideAssetId,
          rightSide: created.rightSideAssetId,
        };
        for (const zone of ZONE_ORDER) {
          const target = ctx.targets[zone.kind];
          const assetId = createdIds[zone.kind];
          if (!target || !assetId) continue;
          await updateAsset({
            assetId,
            orgId,
            sourceSelection: target.sourceSelection,
            ...(target.sideFit ? { sideFit: target.sideFit } : {}),
            ...(target.topBlockHeightPct !== undefined
              ? { topBlockHeightPct: target.topBlockHeightPct }
              : {}),
            ...(target.imageFit ? { imageFit: target.imageFit } : {}),
            ...(target.sourceBodyMargins !== undefined
              ? { sourceBodyMargins: target.sourceBodyMargins }
              : {}),
          });
        }
        toast.success(`Added letterhead "${ctx.name}"`);
        setBundleName("");
        extraction.cleanup();
      } catch (err) {
        console.error("[Bundle Upload] Failed", err);
        toast.error(extractErrorMessage(err, "Failed to add letterhead"));
      } finally {
        pendingUploadRef.current = null;
        setUploading(false);
      }
    },
    onUploadError: (err) => {
      console.error("[Bundle Upload] Upload failed", err);
      pendingUploadRef.current = null;
      setUploading(false);
      toast.error("Upload failed. Please try again.");
    },
  });

  const handleFile = async (file: File) => {
    const isPdf =
      file.name.toLowerCase().endsWith(".pdf") ||
      file.type === "application/pdf";
    if (!isPdf) {
      toast.error("Please upload a PDF.");
      if (inputRef.current) inputRef.current.value = "";
      return;
    }
    await extraction.loadFile(file);
  };

  const handleConfirm = () => {
    if (!pending) return;
    const name =
      bundleName.trim() ||
      pending.sourceFile.name.replace(/\.pdf$/i, "").slice(0, 60) ||
      "Letterhead";

    // Zones the user switched off are dropped from the upload entirely;
    // a flow-beside header is routed to its side zone.
    const targets = zoneSaveTargets(pending);
    const filesToUpload: File[] = [pending.sourceFile];
    for (const zone of ZONE_ORDER) {
      const t = targets[zone.kind];
      if (t) filesToUpload.push(t.file);
    }

    pendingUploadRef.current = {
      sourceFile: pending.sourceFile,
      targets,
      name,
    };
    setUploading(true);
    // startUpload can reject before onUploadError fires (e.g. the upload-URL
    // mutation fails on a dropped session) — without this catch `uploading`
    // stays true forever and the whole card is left disabled.
    void startUpload(filesToUpload).catch((err) => {
      console.error("[Bundle Upload] startUpload failed", err);
      if (pendingUploadRef.current) {
        pendingUploadRef.current = null;
        setUploading(false);
        toast.error(
          extractErrorMessage(err, "Upload failed. Please try again."),
        );
      }
    });
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-3 rounded-lg border border-dashed border-border bg-secondary/20 p-4">
        <FileUp className="size-5 shrink-0 text-primary/40" />
        <div className="flex-1">
          <p className="text-sm font-medium text-primary/80">
            Add new letterheads
          </p>
          <p className="text-xs text-primary/50">
            Upload a PDF letterhead — Morgan auto-detects your header and footer
            and allows you to fine tune the positioning before saving.
          </p>
        </div>
        <Input
          value={bundleName}
          onChange={(e) => setBundleName(e.target.value)}
          placeholder='Add name (e.g. "Sydney office")'
          className="h-9 max-w-[220px] text-xs"
          disabled={rendering || uploading}
        />
        <input
          ref={inputRef}
          type="file"
          accept="application/pdf,.pdf"
          className="hidden"
          disabled={rendering || uploading || !!pending}
          onChange={(e) => {
            const file = e.target.files?.[0];
            // Clear immediately so picking the same file again (after a
            // cancel or a failed render) still fires onChange.
            e.target.value = "";
            if (file) void handleFile(file);
          }}
        />
        <Button
          size="sm"
          variant="outline"
          className="h-9 text-xs"
          disabled={rendering || uploading || !!pending}
          onClick={() => inputRef.current?.click()}
        >
          {rendering ? (
            <Spinner className="mr-1 size-3" />
          ) : (
            <FileUp className="mr-1 size-3" />
          )}
          Upload PDF
        </Button>
      </div>

      {pending && (
        <PendingExtractionPreview
          pending={pending}
          uploading={uploading}
          padding={inheritedPadding}
          onTrimChange={extraction.handleTrimChange}
          onTrimsChange={extraction.applyTrims}
          onMirrorToggle={extraction.setMirrorSides}
          onFlowBesideToggle={extraction.setFlowBeside}
          onPageFitToggle={extraction.setPageFit}
          onBodyMarginsChange={extraction.setBodyMargins}
          onBodyMarginsReset={extraction.resetBodyMargins}
          onToggleZone={extraction.toggleZone}
          onConfirm={handleConfirm}
          onCancel={extraction.cleanup}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Re-crop editor for an existing bundle — same drag editor as the upload
// flow, seeded from the bundle's stored source PDF. Saving replaces each
// zone's image in place (variants keep pointing at the same assets).
// ---------------------------------------------------------------------------

function BundleRecropEditor({
  bundle,
  orgId,
  inheritedPadding,
  onClose,
}: {
  bundle: Bundle;
  orgId: Id<"organizations">;
  inheritedPadding: DocumentPadding;
  onClose: () => void;
}) {
  const extraction = usePendingExtraction();
  const { pending, rendering } = extraction;
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const generateUploadUrl = useConvexMutation(
    api.settings.branding.mutations.generateUploadUrl,
  );
  const { mutateAsync: uploadAssetImage } = useMutation({
    mutationFn: useConvexMutation(
      api.settings.branding.assetMutations.uploadAssetImage,
    ),
  });
  const { mutateAsync: createAsset } = useMutation({
    mutationFn: useConvexMutation(
      api.settings.branding.assetMutations.createAsset,
    ),
  });
  const { mutateAsync: deleteAsset } = useMutation({
    mutationFn: useConvexMutation(
      api.settings.branding.assetMutations.deleteAsset,
    ),
  });
  const { mutateAsync: updateAsset } = useMutation({
    mutationFn: useConvexMutation(
      api.settings.branding.assetMutations.updateAsset,
    ),
  });

  // Pull the source PDF back down and run it through the same render +
  // auto-crop the upload flow uses.
  const sourceAsset = Object.values(bundle.byZone).find(
    (a) => a?.sourceDocumentUrl,
  );
  const sourceUrl: string | null = sourceAsset?.sourceDocumentUrl ?? null;
  const sourceName: string =
    sourceAsset?.sourceDocumentName ?? `${bundle.name}.pdf`;
  const { loadFile } = extraction;
  // Seed from the saved selections so the editor reopens where it was left.
  const seedRef = useRef(
    seedFromAssets(
      Object.values(bundle.byZone).filter((a): a is BundleAsset => !!a),
    ),
  );
  useEffect(() => {
    if (!sourceUrl) {
      setLoadError("This letterhead has no source PDF to re-crop from.");
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(sourceUrl);
        if (!res.ok) throw new Error(`Download failed (${res.status})`);
        const blob = await res.blob();
        if (cancelled) return;
        const file = new File([blob], sourceName, { type: "application/pdf" });
        const ok = await loadFile(file, seedRef.current);
        if (!ok && !cancelled) setLoadError("Couldn't render the source PDF.");
      } catch (err) {
        if (!cancelled)
          setLoadError(
            extractErrorMessage(err, "Couldn't load the source PDF."),
          );
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [sourceUrl, sourceName, loadFile]);

  // Snapshot taken at confirm time. `explicitOff` marks zones the user
  // captured and then unticked — only those delete an existing asset.
  // Zones the auto-detect simply didn't find keep whatever is saved.
  const filesRef = useRef<{
    targets: Partial<Record<ZoneKind, ZoneSaveTarget>>;
    explicitOff: Record<ZoneKind, boolean>;
  } | null>(null);

  const { startUpload } = useUploadFiles(generateUploadUrl, {
    onUploadComplete: async (uploaded) => {
      const snapshot = filesRef.current;
      try {
        if (!snapshot) throw new Error("Missing upload context");
        const { targets, explicitOff } = snapshot;
        const storageIdFor = (f: File | undefined) => {
          if (!f) return null;
          const hit = uploaded.find((u) => u.name === f.name);
          return hit ? (hit.response as UploadResponse).storageId : null;
        };
        for (const z of ZONE_ORDER) {
          const existing = bundle.byZone[z.kind];
          const target = targets[z.kind];
          const storageId = storageIdFor(target?.file);
          const settings = target
            ? {
                sourceSelection: target.sourceSelection,
                ...(target.sideFit ? { sideFit: target.sideFit } : {}),
                ...(target.topBlockHeightPct !== undefined
                  ? { topBlockHeightPct: target.topBlockHeightPct }
                  : {}),
                ...(target.defaultPageRule
                  ? { defaultPageRule: target.defaultPageRule }
                  : {}),
                ...(target.imageFit ? { imageFit: target.imageFit } : {}),
              }
            : {};
          // Null clears a saved body column on update; a new asset simply
          // has none.
          const bodyMargins =
            target?.sourceBodyMargins !== undefined
              ? { sourceBodyMargins: target.sourceBodyMargins }
              : {};
          if (storageId && existing) {
            await uploadAssetImage({ assetId: existing._id, orgId, storageId });
            await updateAsset({
              assetId: existing._id,
              orgId,
              ...settings,
              ...bodyMargins,
            });
          } else if (storageId) {
            await createAsset({
              orgId,
              zoneKind: z.kind,
              name: `${bundle.name} — ${z.label}`,
              bundleName: bundle.name,
              imageStorageId: storageId,
              ...settings,
              ...(target?.sourceBodyMargins
                ? { sourceBodyMargins: target.sourceBodyMargins }
                : {}),
            });
          } else if (existing && explicitOff[z.kind]) {
            // Captured, then switched off by the user — drop it.
            await deleteAsset({ assetId: existing._id, orgId });
          } else if (
            existing &&
            !target &&
            existing.sourceSelection?.capturedAs === "header" &&
            targets.header
          ) {
            // The header used to be saved as a flow-beside side block and
            // is now a plain header again — retire the old side asset.
            await deleteAsset({ assetId: existing._id, orgId });
          } else if (
            existing &&
            z.kind === "header" &&
            !target &&
            (targets.leftSide?.sourceSelection.capturedAs === "header" ||
              targets.rightSide?.sourceSelection.capturedAs === "header")
          ) {
            // The header moved to a side block — retire the old header asset.
            await deleteAsset({ assetId: existing._id, orgId });
          }
        }
        toast.success(`Updated "${bundle.name}"`);
        extraction.cleanup();
        onClose();
      } catch (err) {
        console.error("[Bundle Recrop] Failed", err);
        toast.error(extractErrorMessage(err, "Failed to save changes"));
      } finally {
        filesRef.current = null;
        setSaving(false);
      }
    },
    onUploadError: (err) => {
      console.error("[Bundle Recrop] Upload failed", err);
      filesRef.current = null;
      setSaving(false);
      toast.error("Upload failed. Please try again.");
    },
  });

  const handleConfirm = () => {
    if (!pending) return;
    const targets = zoneSaveTargets(pending);
    const off = pending.disabledZones ?? {};
    const explicitOff: Record<ZoneKind, boolean> = {
      header: !!pending.headerBlobUrl && !!off.header,
      footer: !!pending.footerBlobUrl && !!off.footer,
      leftSide: !!pending.leftSideBlobUrl && !!off.leftSide,
      rightSide: !!pending.rightSideBlobUrl && !!off.rightSide,
    };
    const toUpload = ZONE_ORDER.map((z) => targets[z.kind]?.file).filter(
      (f): f is File => !!f,
    );
    filesRef.current = { targets, explicitOff };
    setSaving(true);
    if (toUpload.length === 0) {
      // Nothing to upload — still apply removals for switched-off zones.
      void Promise.resolve().then(async () => {
        try {
          for (const zone of ZONE_ORDER) {
            const existing = bundle.byZone[zone.kind];
            if (existing && explicitOff[zone.kind])
              await deleteAsset({ assetId: existing._id, orgId });
          }
          toast.success(`Updated "${bundle.name}"`);
          extraction.cleanup();
          onClose();
        } catch (err) {
          toast.error(extractErrorMessage(err, "Failed to save changes"));
        } finally {
          filesRef.current = null;
          setSaving(false);
        }
      });
      return;
    }
    void startUpload(toUpload).catch((err) => {
      console.error("[Bundle Recrop] startUpload failed", err);
      if (filesRef.current) {
        filesRef.current = null;
        setSaving(false);
        toast.error(
          extractErrorMessage(err, "Upload failed. Please try again."),
        );
      }
    });
  };

  const handleCancel = () => {
    extraction.cleanup();
    onClose();
  };

  if (loadError) {
    return (
      <div className="flex items-center justify-between gap-3 rounded-lg border border-dashed border-border bg-secondary/20 px-4 py-3 text-xs text-primary/60">
        <span>{loadError}</span>
        <Button
          size="sm"
          variant="outline"
          className="h-7 text-xs"
          onClick={onClose}
        >
          Close
        </Button>
      </div>
    );
  }

  if (!pending || rendering) {
    return (
      <div className="flex items-center gap-2 rounded-lg border border-dashed border-border bg-secondary/20 px-4 py-6 text-xs text-primary/50">
        <Spinner className="size-3.5" />
        Preparing the source PDF for editing…
      </div>
    );
  }

  return (
    <PendingExtractionPreview
      pending={pending}
      uploading={saving}
      padding={inheritedPadding}
      title={`Edit crop — ${bundle.name}`}
      confirmLabel="Save changes"
      onTrimChange={extraction.handleTrimChange}
      onTrimsChange={extraction.applyTrims}
      onMirrorToggle={extraction.setMirrorSides}
      onFlowBesideToggle={extraction.setFlowBeside}
      onPageFitToggle={extraction.setPageFit}
      onBodyMarginsChange={extraction.setBodyMargins}
      onBodyMarginsReset={extraction.resetBodyMargins}
      onToggleZone={extraction.toggleZone}
      onConfirm={handleConfirm}
      onCancel={handleCancel}
    />
  );
}

// ---------------------------------------------------------------------------
// Bundle card — one named letterhead with 4 zone rows
// ---------------------------------------------------------------------------

function BundleCard({
  bundle,
  orgId,
  canEdit,
  isFirmDefault,
  isFavourite,
  forceCollapsed,
  inheritedPadding,
}: {
  bundle: Bundle;
  orgId: Id<"organizations">;
  canEdit: boolean;
  isFirmDefault: boolean;
  isFavourite: boolean;
  forceCollapsed?: boolean;
  inheritedPadding: DocumentPadding;
}) {
  const { mutateAsync: updateAsset } = useMutation({
    mutationFn: useConvexMutation(
      api.settings.branding.assetMutations.updateAsset,
    ),
  });
  const { mutateAsync: deleteAsset } = useMutation({
    mutationFn: useConvexMutation(
      api.settings.branding.assetMutations.deleteAsset,
    ),
  });
  const { mutateAsync: setFirmDefaultLetterheadBundle } = useMutation({
    mutationFn: useConvexMutation(
      api.settings.branding.mutations.setFirmDefaultLetterheadBundle,
    ),
  });
  const { mutateAsync: toggleFavouriteLetterheadBundle } = useMutation({
    mutationFn: useConvexMutation(
      api.settings.branding.mutations.toggleFavouriteLetterheadBundle,
    ),
  });

  const handleToggleFirmDefault = async () => {
    try {
      await setFirmDefaultLetterheadBundle({
        orgId,
        bundleName: isFirmDefault ? null : bundle.name,
      });
      toast.success(
        isFirmDefault
          ? "Cleared firm default"
          : `Set "${bundle.name}" as firm default`,
      );
    } catch (err) {
      toast.error(extractErrorMessage(err, "Failed to set firm default"));
    }
  };

  const handleToggleFavourite = async () => {
    try {
      await toggleFavouriteLetterheadBundle({
        orgId,
        bundleName: bundle.name,
      });
    } catch (err) {
      toast.error(extractErrorMessage(err, "Failed to update favourite"));
    }
  };

  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState(bundle.name);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const hasSourcePdf = Object.values(bundle.byZone).some(
    (a) => a?.sourceDocumentUrl,
  );
  const [localCollapsed, setLocalCollapsed] = useState<boolean | null>(null);
  const collapsed = localCollapsed ?? forceCollapsed ?? false;

  useEffect(() => {
    setLocalCollapsed(null);
  }, [forceCollapsed]);

  // Show a quick summary in the collapsed state so the user can tell
  // bundles apart at a glance (e.g. "3/4 zones, Header set to First only").
  const zoneSummary = (() => {
    const filled = Object.values(bundle.byZone).filter(
      (a) => a?.imageUrl,
    ).length;
    return `${filled}/4 zones`;
  })();

  const persistName = async () => {
    const trimmed = nameDraft.trim();
    if (!trimmed || trimmed === bundle.name) {
      setEditingName(false);
      setNameDraft(bundle.name);
      return;
    }
    try {
      // Rename every asset in the bundle so they stay grouped.
      const ids = Object.values(bundle.byZone)
        .filter((a): a is BundleAsset => !!a)
        .map((a) => a._id);
      await Promise.all(
        ids.map((id) =>
          updateAsset({
            assetId: id,
            orgId,
            bundleName: trimmed,
          }),
        ),
      );
      toast.success("Letterhead renamed");
    } catch (err) {
      toast.error(extractErrorMessage(err, "Failed to rename"));
      setNameDraft(bundle.name);
    } finally {
      setEditingName(false);
    }
  };

  const deleteBundle = async () => {
    try {
      const ids = Object.values(bundle.byZone)
        .filter((a): a is BundleAsset => !!a)
        .map((a) => a._id);
      await Promise.all(ids.map((id) => deleteAsset({ assetId: id, orgId })));
      toast.success(`Deleted "${bundle.name}"`);
    } catch (err) {
      toast.error(extractErrorMessage(err, "Failed to delete"));
    }
  };

  return (
    <div className="rounded-xl border border-border bg-background p-4">
      <div
        className={cn(
          "flex items-center justify-between gap-3",
          !collapsed && "mb-4",
        )}
      >
        <div className="flex min-w-0 flex-1 items-center gap-2">
          <button
            type="button"
            onClick={() => setLocalCollapsed(!collapsed)}
            className="shrink-0 rounded p-0.5 text-primary/40 transition hover:bg-secondary hover:text-primary/70"
            title={collapsed ? "Expand" : "Collapse"}
            aria-label={collapsed ? "Expand letterhead" : "Collapse letterhead"}
          >
            {collapsed ? (
              <ChevronRight className="size-4" />
            ) : (
              <ChevronDown className="size-4" />
            )}
          </button>
          {editingName ? (
            <Input
              autoFocus
              value={nameDraft}
              onChange={(e) => setNameDraft(e.target.value)}
              onBlur={() => void persistName()}
              onKeyDown={(e) => {
                if (e.key === "Enter") void persistName();
                if (e.key === "Escape") {
                  setNameDraft(bundle.name);
                  setEditingName(false);
                }
              }}
              className="h-7 max-w-[280px] text-sm font-medium"
            />
          ) : (
            <button
              type="button"
              onClick={() => canEdit && setEditingName(true)}
              className="min-w-0 truncate whitespace-nowrap text-sm font-medium text-primary/80 hover:text-primary"
              disabled={!canEdit}
              title={bundle.name}
            >
              {bundle.name}
            </button>
          )}
          {bundle.sourceDocumentName && (
            <span
              className="max-w-[220px] shrink-0 truncate whitespace-nowrap rounded bg-secondary px-1.5 py-0.5 text-[10px] text-primary/40"
              title={bundle.sourceDocumentName}
            >
              {bundle.sourceDocumentName}
            </span>
          )}
          {isFirmDefault && (
            <span className="inline-flex shrink-0 items-center gap-1 rounded bg-emerald-100 px-1.5 py-0.5 text-[10px] font-medium text-emerald-700 dark:bg-emerald-950/30 dark:text-emerald-300">
              Firm default
            </span>
          )}
          {collapsed && (
            <span className="shrink-0 text-[11px] text-primary/40">
              {zoneSummary}
            </span>
          )}
        </div>
        {canEdit && (
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => void handleToggleFirmDefault()}
              className={cn(
                "rounded px-2 py-1 text-[11px] transition",
                isFirmDefault
                  ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300"
                  : "border border-border bg-background text-primary/60 hover:bg-secondary",
              )}
              title={
                isFirmDefault
                  ? "Clear firm default — documents will fall back to no letterhead"
                  : "Make this the firm default — used by every document with no override"
              }
            >
              {isFirmDefault ? "Clear default" : "Set as firm default"}
            </button>
            <button
              type="button"
              onClick={() => void handleToggleFavourite()}
              className={cn(
                "rounded p-1 transition hover:bg-secondary",
                isFavourite ? "text-amber-500" : "text-primary/40",
              )}
              title={
                isFavourite
                  ? "Remove from favourites"
                  : "Favourite — surfaces at the top of the per-document picker"
              }
            >
              <Star className="size-4" />
            </button>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <button
                  type="button"
                  className="rounded p-1 text-primary/40 transition hover:bg-destructive/10 hover:text-destructive"
                  title="Delete this letterhead"
                >
                  <Trash2 className="size-4" />
                </button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>
                    Delete &quot;{bundle.name}&quot;?
                  </AlertDialogTitle>
                  <AlertDialogDescription>
                    All four zones for this letterhead will be deleted. Any
                    variant using them will fall back to empty for those zones.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={() => void deleteBundle()}
                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  >
                    Delete letterhead
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        )}
      </div>

      {/* Letterheads with a stored source PDF open straight into the same
       *  drag-to-select editor the upload flow uses. The per-zone render
       *  settings (crop %, scale, page rule) sit behind a toggle below. */}
      {!collapsed && hasSourcePdf && (
        <div className="flex flex-col gap-3">
          <BundleRecropEditor
            bundle={bundle}
            orgId={orgId}
            inheritedPadding={inheritedPadding}
            onClose={() => setLocalCollapsed(true)}
          />
          <button
            type="button"
            onClick={() => setShowAdvanced((o) => !o)}
            className="flex items-center gap-1.5 self-start rounded px-2 py-1 text-xs text-primary/50 transition hover:bg-secondary hover:text-primary/70"
          >
            {showAdvanced ? (
              <ChevronDown className="size-3.5" />
            ) : (
              <ChevronRight className="size-3.5" />
            )}
            Advanced zone settings (crop %, scale, page rule)
          </button>
          {showAdvanced && (
            <div className="flex flex-col gap-2">
              {ZONE_ORDER.map((zone) => (
                <ZoneCard
                  key={zone.kind}
                  bundle={bundle}
                  zone={zone.kind}
                  label={zone.label}
                  tall={zone.tall}
                  orgId={orgId}
                  canEdit={canEdit}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {!collapsed && !hasSourcePdf && (
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start">
          {/* Live preview — every zone composed on a sample page, exactly
           *  as the exporters lay it out. Updates as the controls change. */}
          <BundlePreviewColumn bundle={bundle} padding={inheritedPadding} />
          <div className="flex min-w-0 flex-1 flex-col gap-2">
            {ZONE_ORDER.map((zone) => (
              <ZoneCard
                key={zone.kind}
                bundle={bundle}
                zone={zone.kind}
                label={zone.label}
                tall={zone.tall}
                orgId={orgId}
                canEdit={canEdit}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * The sample-page previews for one bundle: one page per distinct layout the
 * bundle's default page rules produce (page 1 / other pages / even pages).
 */
function BundlePreviewColumn({
  bundle,
  padding,
}: {
  bundle: Bundle;
  padding: DocumentPadding;
}) {
  // Re-key every asset to a synthetic bundle name so grouping quirks in
  // legacy data (missing bundleName) can't drop zones from the preview.
  const fragments = useMemo(() => {
    const assets = Object.values(bundle.byZone)
      .filter((a) => a?.imageUrl)
      .map((a) => ({ ...a, bundleName: "__bundle_preview__" }));
    return buildBundleFragments("__bundle_preview__", assets);
  }, [bundle]);

  const combos = useMemo(() => derivePageLayoutCombos(fragments), [fragments]);

  if (fragments.length === 0) return null;

  const pageForClass = { first: 1, other: 3, even: 2 } as const;
  const classLabel = {
    first: "Page 1",
    other: "Other pages",
    even: "Even pages",
  } as const;

  return (
    <div className="flex shrink-0 flex-wrap items-start justify-center gap-4 lg:flex-col lg:items-center">
      {combos.map((combo) => (
        <LetterheadSamplePreview
          key={combo.pageClass}
          fragments={fragments}
          // The firm's margins with this page's letterhead-measured body
          // column laid over them, as the exporters resolve it.
          padding={resolveDocumentPadding(
            paddingAsSource(padding),
            null,
            null,
            variantPaddingTiers(fragments, pageForClass[combo.pageClass]),
          )}
          pageIndex={pageForClass[combo.pageClass]}
          width={252}
          label={combos.length > 1 ? classLabel[combo.pageClass] : "Preview"}
        />
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// ZoneCard — one row inside a bundle: preview + page rule + scale + replace + trash
// ---------------------------------------------------------------------------

/**
 * A compact slider row for the zone card: a labelled range input flanked by
 * ± nudge buttons, with a caller-supplied value display on the right.
 */
function ZoneSliderRow({
  label,
  value,
  min,
  max,
  step,
  nudgeStep,
  disabled,
  onCommit,
  rightSlot,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  step: number;
  nudgeStep: number;
  disabled?: boolean;
  onCommit: (value: number) => void;
  rightSlot: React.ReactNode;
}) {
  // Snap to 4 decimals so repeated nudges don't accumulate float noise.
  const commit = (v: number) =>
    onCommit(Math.max(min, Math.min(max, Math.round(v * 10000) / 10000)));
  return (
    <div className="mt-2 flex items-center gap-1.5 text-[11px] text-primary/40">
      <span className="w-16 shrink-0">{label}</span>
      <SliderNudgeButton
        direction="down"
        title={`Decrease ${label.toLowerCase()}`}
        onClick={() => commit(value - nudgeStep)}
        disabled={disabled || value <= min}
      />
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        disabled={disabled}
        onChange={(e) => commit(parseFloat(e.target.value))}
        className="h-1 flex-1 accent-primary"
      />
      <SliderNudgeButton
        direction="up"
        title={`Increase ${label.toLowerCase()}`}
        onClick={() => commit(value + nudgeStep)}
        disabled={disabled || value >= max}
      />
      {rightSlot}
    </div>
  );
}

/**
 * Editable percentage field for the Scale row — type a value (clamped
 * 30–100) or step it with ± buttons.
 */
function ScaleValueInput({
  scale,
  disabled,
  onCommit,
}: {
  scale: number;
  disabled?: boolean;
  onCommit: (scale: number) => void;
}) {
  const [text, setText] = useState(String(Math.round(scale * 100)));
  useEffect(() => {
    setText(String(Math.round(scale * 100)));
  }, [scale]);

  const commit = () => {
    const n = parseInt(text, 10);
    if (Number.isNaN(n)) {
      setText(String(Math.round(scale * 100)));
      return;
    }
    const clamped = Math.max(30, Math.min(100, n));
    setText(String(clamped));
    onCommit(clamped / 100);
  };

  return (
    <div className="flex items-center gap-0.5">
      <SliderNudgeButton
        direction="down"
        title="Decrease scale"
        onClick={() =>
          onCommit(Math.max(0.3, (Math.round(scale * 100) - 1) / 100))
        }
        disabled={disabled || scale <= 0.3}
      />
      <input
        type="text"
        inputMode="numeric"
        value={text}
        disabled={disabled}
        onChange={(e) => setText(e.target.value.replace(/[^0-9]/g, ""))}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            commit();
            e.currentTarget.blur();
          }
        }}
        className="w-8 rounded border border-border bg-background px-1 py-0.5 text-center text-[11px] tabular-nums text-primary/70 disabled:opacity-50"
      />
      <span>%</span>
      <SliderNudgeButton
        direction="up"
        title="Increase scale"
        onClick={() =>
          onCommit(Math.min(1, (Math.round(scale * 100) + 1) / 100))
        }
        disabled={disabled || scale >= 1}
      />
    </div>
  );
}

function ZoneCard({
  bundle,
  zone,
  label,
  tall,
  orgId,
  canEdit,
}: {
  bundle: Bundle;
  zone: ZoneKind;
  label: string;
  tall?: boolean;
  orgId: Id<"organizations">;
  canEdit: boolean;
}) {
  const asset = bundle.byZone[zone];

  const { mutateAsync: updateAsset } = useMutation({
    mutationFn: useConvexMutation(
      api.settings.branding.assetMutations.updateAsset,
    ),
  });
  const { mutateAsync: deleteAsset } = useMutation({
    mutationFn: useConvexMutation(
      api.settings.branding.assetMutations.deleteAsset,
    ),
  });
  const { mutateAsync: uploadAssetImage } = useMutation({
    mutationFn: useConvexMutation(
      api.settings.branding.assetMutations.uploadAssetImage,
    ),
  });
  const { mutateAsync: createAsset } = useMutation({
    mutationFn: useConvexMutation(
      api.settings.branding.assetMutations.createAsset,
    ),
  });
  const generateUploadUrl = useConvexMutation(
    api.settings.branding.mutations.generateUploadUrl,
  );

  const inputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const pendingRef = useRef<File | null>(null);

  const { startUpload } = useUploadFiles(generateUploadUrl, {
    onUploadComplete: async (uploaded) => {
      if (inputRef.current) inputRef.current.value = "";
      const file = pendingRef.current;
      try {
        if (!file) throw new Error("Missing pending file");
        const match = uploaded.find((u) => u.name === file.name);
        if (!match) throw new Error("Upload result not found");
        const storageId = (match.response as UploadResponse).storageId;
        if (asset) {
          await uploadAssetImage({
            assetId: asset._id,
            orgId,
            storageId,
          });
          toast.success(`${label} replaced`);
        } else {
          // No asset for this zone yet — create one in the same bundle.
          await createAsset({
            orgId,
            zoneKind: zone,
            name: `${bundle.name} — ${label}`,
            bundleName: bundle.name,
            imageStorageId: storageId,
          });
          toast.success(`${label} added`);
        }
      } catch (err) {
        toast.error(extractErrorMessage(err, "Failed to save image"));
      } finally {
        pendingRef.current = null;
        setBusy(false);
      }
    },
    onUploadError: () => {
      pendingRef.current = null;
      setBusy(false);
      toast.error("Upload failed. Please try again.");
    },
  });

  // Replace an existing zone's image (or add one for an empty zone) from a
  // plain image file picker. Crop/scale is then adjusted with the inline
  // controls below — no separate re-crop step needed.
  const handleFile = async (file: File) => {
    const isImage =
      file.type.startsWith("image/") ||
      /\.(png|jpe?g|gif|webp|svg)$/i.test(file.name);
    if (!isImage) {
      toast.error("Please upload an image file (PNG, JPG, GIF, WebP, or SVG).");
      if (inputRef.current) inputRef.current.value = "";
      return;
    }
    setBusy(true);
    pendingRef.current = file;
    try {
      await startUpload([file]);
    } catch (err) {
      // onUploadError may have already handled this; only report if not.
      if (pendingRef.current) {
        console.error("[Zone Upload] startUpload failed", err);
        setBusy(false);
        pendingRef.current = null;
        toast.error(
          extractErrorMessage(err, "Upload failed. Please try again."),
        );
      }
    }
  };

  const scale = asset?.imageScale ?? 1;
  const sideFit: SideFit = asset?.sideFit ?? "fullHeight";
  const isTopBlock = !!tall && sideFit === "topBlock";
  const topBlockPct = clampTopBlockHeightPct(
    asset?.topBlockHeightPct ?? DEFAULT_TOP_BLOCK_HEIGHT_PCT,
  );
  // topBlock source crop — reuses the asset's imageCrop* fields (which are
  // otherwise header/footer-only) so the user can isolate the relevant
  // section of a full-height strip instead of squeezing the whole image.
  const topBlockCrop = normalizeSideCrop({
    top: asset?.imageCropTop ?? 0,
    bottom: asset?.imageCropBottom ?? 0,
    left: asset?.imageCropLeft ?? 0,
    right: asset?.imageCropRight ?? 0,
  });
  const cropTop = asset?.imageCropTop ?? 0;
  const cropBottom = asset?.imageCropBottom ?? 0;
  const cropLeft = asset?.imageCropLeft ?? 0;
  const cropRight = asset?.imageCropRight ?? 0;

  return (
    <div className="rounded-lg border border-border bg-secondary/20 p-3">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-xs text-primary/60">
          <Eye className="size-3.5" />
          <span className="font-medium text-primary/80">{label}</span>
        </div>
        <div className="flex items-center gap-2">
          {canEdit && (
            <>
              <input
                ref={inputRef}
                type="file"
                accept="image/png,image/jpeg,image/gif,image/webp,image/svg+xml"
                className="hidden"
                disabled={busy}
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  // Clear immediately so re-picking the same file works.
                  e.target.value = "";
                  if (file) void handleFile(file);
                }}
              />
              <button
                type="button"
                disabled={busy}
                onClick={() => inputRef.current?.click()}
                className="flex items-center gap-1 rounded px-2 py-1 text-xs text-primary/50 transition hover:bg-secondary disabled:opacity-50"
                title={
                  asset
                    ? `Replace ${label.toLowerCase()} image`
                    : `Upload ${label.toLowerCase()}`
                }
              >
                {busy ? (
                  <Spinner className="size-3" />
                ) : asset ? (
                  <Pencil className="size-3" />
                ) : (
                  <Upload className="size-3" />
                )}
                {asset ? "Replace" : "Upload"}
              </button>
              {asset && (
                <>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <button
                        type="button"
                        className="rounded p-1 text-primary/40 transition hover:bg-destructive/10 hover:text-destructive"
                        title="Remove this zone"
                      >
                        <Trash2 className="size-3.5" />
                      </button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>
                          Remove {label.toLowerCase()}?
                        </AlertDialogTitle>
                        <AlertDialogDescription>
                          The {label.toLowerCase()} image for &quot;
                          {bundle.name}&quot; will be deleted. Variants using it
                          will fall back to empty for this zone.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                          onClick={() =>
                            void deleteAsset({
                              assetId: asset._id,
                              orgId,
                            })
                              .then(() => toast.success(`${label} removed`))
                              .catch((err) =>
                                toast.error(extractErrorMessage(err, "Failed")),
                              )
                          }
                          className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                        >
                          Remove
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </>
              )}
            </>
          )}
        </div>
      </div>

      {/* Compact thumbnail — placement/composition now lives in the
       *  bundle-level sample preview beside these controls. */}
      {asset?.imageUrl && (
        <div className="mt-2 flex justify-center rounded border border-dashed border-border bg-white p-2">
          <img
            src={asset.imageUrl}
            alt={`${label} image`}
            className={cn(
              "w-auto max-w-full object-contain",
              tall ? "max-h-24" : "max-h-14",
            )}
          />
        </div>
      )}

      {/* Header/footer render-time crop — fractions of the image, applied
       *  identically in the preview and the export. */}
      {asset && asset.imageUrl && !tall && (
        <>
          {(
            [
              ["Top crop", cropTop, "imageCropTop"],
              ["Bottom crop", cropBottom, "imageCropBottom"],
              ["Left crop", cropLeft, "imageCropLeft"],
              ["Right crop", cropRight, "imageCropRight"],
            ] as const
          ).map(([rowLabel, value, field]) => (
            <ZoneSliderRow
              key={field}
              label={rowLabel}
              value={value}
              min={0}
              max={0.4}
              step={0.005}
              nudgeStep={0.005}
              disabled={!canEdit}
              onCommit={(v) =>
                void updateAsset({
                  assetId: asset._id,
                  orgId,
                  [field]: v,
                }).catch(() => {})
              }
              rightSlot={
                <span className="w-10 text-right tabular-nums">
                  {Math.round(value * 100)}%
                </span>
              }
            />
          ))}
        </>
      )}

      {/* Side fit — full-height band vs partial-height top block (sides only).
       *  topBlock renders on the FIRST page at natural aspect ratio and body
       *  text wraps around it: narrow beside the block, full width below. */}
      {asset && asset.imageUrl && tall && (
        <div className="mt-2 flex items-center gap-2 text-[11px] text-primary/40">
          <span className="w-24 shrink-0">Fit</span>
          <Select
            value={sideFit}
            onValueChange={(v) => {
              const nextFit = v as SideFit;
              void updateAsset({
                assetId: asset._id,
                orgId,
                sideFit: nextFit,
                // A top block is first-page-anchored (its text-wrap geometry
                // lives at the top of the body flow), so pin the rule.
                ...(nextFit === "topBlock"
                  ? { defaultPageRule: "firstOnly" }
                  : {}),
              }).catch((err) =>
                toast.error(extractErrorMessage(err, "Failed to save")),
              );
            }}
            disabled={!canEdit}
          >
            <SelectTrigger className="h-6 flex-1 text-[11px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="fullHeight" className="text-xs">
                Full page height
              </SelectItem>
              <SelectItem value="topBlock" className="text-xs">
                Partial height — text wraps below
              </SelectItem>
            </SelectContent>
          </Select>
        </div>
      )}

      {/* topBlock sides: source crop + on-page block height. Crop first —
       *  it selects WHICH part of the artwork is the block (e.g. just the
       *  top logo/address section of a full-height strip); block height
       *  then sets how far down the page that section extends. */}
      {asset && asset.imageUrl && isTopBlock && (
        <>
          <ZoneSliderRow
            label="Crop top"
            value={topBlockCrop.top}
            min={0}
            max={0.9}
            step={0.01}
            nudgeStep={0.01}
            disabled={!canEdit}
            onCommit={(v) =>
              void updateAsset({
                assetId: asset._id,
                orgId,
                imageCropTop: v,
              }).catch(() => {})
            }
            rightSlot={
              <span className="w-10 text-right tabular-nums">
                {Math.round(topBlockCrop.top * 100)}%
              </span>
            }
          />
          <ZoneSliderRow
            label="Crop bottom"
            value={topBlockCrop.bottom}
            min={0}
            max={0.9}
            step={0.01}
            nudgeStep={0.01}
            disabled={!canEdit}
            onCommit={(v) =>
              void updateAsset({
                assetId: asset._id,
                orgId,
                imageCropBottom: v,
              }).catch(() => {})
            }
            rightSlot={
              <span className="w-10 text-right tabular-nums">
                {Math.round(topBlockCrop.bottom * 100)}%
              </span>
            }
          />
          <p className="mt-1 text-[11px] leading-snug text-primary/40">
            Crop the image to the section that should form the block — e.g. cut
            the empty bottom of a full-height strip so only the top artwork
            remains.
          </p>
          <ZoneSliderRow
            label="Block height"
            value={topBlockPct}
            min={0.05}
            max={0.9}
            step={0.01}
            nudgeStep={0.01}
            disabled={!canEdit}
            onCommit={(v) =>
              void updateAsset({
                assetId: asset._id,
                orgId,
                topBlockHeightPct: v,
              }).catch(() => {})
            }
            rightSlot={
              <span className="w-10 text-right tabular-nums">
                {Math.round(topBlockPct * 100)}%
              </span>
            }
          />
          <p className="mt-1 text-[11px] leading-snug text-primary/40">
            Shows on the first page only. Body text flows beside the block and
            uses the full width below it.
          </p>
        </>
      )}

      {/* Scale slider (only when an image is present; not applicable to
       *  partial-height blocks, which size from the height setting) */}
      {asset && asset.imageUrl && !isTopBlock && (
        <ZoneSliderRow
          label="Scale"
          value={scale}
          min={0.3}
          max={1}
          step={0.05}
          nudgeStep={0.01}
          disabled={!canEdit}
          onCommit={(v) =>
            void updateAsset({
              assetId: asset._id,
              orgId,
              imageScale: v,
            }).catch(() => {})
          }
          rightSlot={
            <ScaleValueInput
              scale={scale}
              disabled={!canEdit}
              onCommit={(v) =>
                void updateAsset({
                  assetId: asset._id,
                  orgId,
                  imageScale: v,
                }).catch(() => {})
              }
            />
          }
        />
      )}

      {/* Default page rule — used when no variant applies (i.e. the
       * backwards-compat path where the user has letterheads but hasn't
       * built any variants yet). Variants always override this. */}
      {asset && (
        <div className="mt-2 flex items-center gap-2 text-[11px] text-primary/40">
          <span className="w-24 shrink-0">Default rule</span>
          <Select
            value={asset.defaultPageRule ?? "all"}
            onValueChange={(v) =>
              void updateAsset({
                assetId: asset._id,
                orgId,
                defaultPageRule: v as PageRule,
              }).catch((err) =>
                toast.error(extractErrorMessage(err, "Failed to save")),
              )
            }
            disabled={!canEdit}
          >
            <SelectTrigger className="h-6 flex-1 text-[11px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {ASSET_DEFAULT_PAGE_RULES.map((opt) => (
                <SelectItem
                  key={opt.value}
                  value={opt.value}
                  className="text-xs"
                >
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <span className="text-primary/30">overridden by variants</span>
        </div>
      )}
    </div>
  );
}
